import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Portal, Box, Typography, Button, Tabs, Tab,
  IconButton, TextField, CircularProgress, Divider, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QRCode from "react-qr-code";
import CryptoJS from "crypto-js";
import { useTranslation } from "react-i18next";
import { DS, inkButtonSx, outlineButtonSx, fieldSx, tabSx as dsTabSx } from "./dsModal.js";
import { useBankId, MODE_SAME_DEVICE, MODE_OTHER_DEVICE, HINT_AWAITING_RETURN } from "../../hooks/useBankId";
import { isMobileDevice, isIOSDevice, openBankIdApp, releaseResume } from "../../utils/bankIdSession";
import { bankIdHints, AWAITING_RETURN_GRACE_MS } from "../../utils/bankIdHints";
import bankIdLogo from "../../assets/logoBankId.png";

// ── BankID animated QR per RP API v6 spec ────────────────────────────────────
// Per BankID official SampleCode (BankIDService.java):
//   qrAuthCode = HMAC-SHA256(key=qrStartSecret_as_ascii, msg=t_as_ascii_string)
//   QR string  = "bankid." + qrStartToken + "." + t + "." + qrAuthCode
// - qrStartSecret is used AS-IS as a UTF-8 string key (UUID with hyphens, 36 bytes)
// - The message is elapsed seconds as a decimal string: "0", "1", "2", ...
// - Each frame is computed independently (no chaining from previous frame)
function computeQrContent(qrStartToken, qrStartSecret, seconds) {
  const qrAuthCode = CryptoJS.HmacSHA256(
    seconds.toString(),  // message: elapsed seconds as decimal string
    qrStartSecret        // key: UUID string with hyphens, used as UTF-8 bytes
  ).toString();
  return `bankid.${qrStartToken}.${seconds}.${qrAuthCode}`;
}

const modeForTab = (tab) => (tab === 1 ? MODE_SAME_DEVICE : MODE_OTHER_DEVICE);

// ── Component ─────────────────────────────────────────────────────────────────
// Each tab runs its own order: "This device" gets a same-device order (autoStartToken only,
// server-side returnUrl), "QR" a QR order (QR tokens only). Switching tab replaces the order.
//
// autoLaunch: the user just chose "open BankID on this device", so open the app as soon as the
//   order exists instead of asking for a second tap. Choosing the "This device" tab is the same
//   explicit choice. The button stays as the fallback if the browser blocks the launch.
// resumeOrderRef: an order this browser already started (reload, or the BankID app's return in
//   a new tab). It is resumed — never relaunched, never replaced by a new order.
// scope: which dialog owns the order, so only that dialog resumes it after a reload.
// purpose "link": a signed-in user verifies their identity (the order is collected through
//   /BankId/Link, onSuccess gets no token). Default "auth" logs in — or, on a first BankID login,
//   creates the account — and onSuccess(token, { isNewUser }) follows.
// hidden: the order runs but nothing is shown yet — iPhone/iPad start it while the login dialog
//   is still in front, so the user's one tap there can open the app (see controlRef).
// controlRef: { launch, selectTab, cancel } for the dialog in front while this one is hidden.
const BankIdAuthModal = ({ open, onClose, onSuccess, initialTab = null, autoLaunch = false, resumeOrderRef = null, scope = "default", purpose = "auth", hidden = false, controlRef = null }) => {
  const { t, i18n } = useTranslation();
  const HINTS = bankIdHints(i18n.language);

  const bankId = useBankId();
  // The shared DS dialog theme — the same tokens loginModal.js uses, so the
  // two halves of one identification flow cannot drift apart. See dsModal.js.
  const SURFACE = DS.sheet;
  const SURFACE_MUTED = DS.canvas;
  const BORDER = DS.ruleEdge;
  const TEXT = DS.ink;
  const MUTED = DS.muted;
  // Green survives ONLY as a state mark here (the verified/progress signals);
  // every fill is ink. GREEN_HOVER/GREEN_BORDER are gone with the fills.
  const GREEN = DS.green;

  // BankID spec: default to "This device" on mobile browsers (incl. iPadOS) — QR is for desktop-to-mobile
  const isMobileBrowser = isMobileDevice();
  const defaultTab = isMobileBrowser ? 1 : 0;
  const [subTab, setSubTab] = useState(defaultTab);  // 0 = QR  |  1 = This device
  const [step, setStep] = useState("idle");       // idle | polling | reg_required
  const [resuming, setResuming] = useState(false);
  const [showAwaitingHelp, setShowAwaitingHelp] = useState(false);
  const [qrContent, setQrContent] = useState("");
  const [error, setError] = useState("");

  // Registration fields (new-user flow)
  const [regEmail, setRegEmail] = useState("");
  const [regPersonalContact, setRegPersonalContact] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [regError, setRegError] = useState("");

  const orderRefRef = useRef(null);
  const completionDataRef = useRef(null);
  const startTimeRef = useRef(null);
  const qrTimerRef = useRef(null);
  const emailInputRef = useRef(null);
  const launchWhenReadyRef = useRef(false);   // open the app once this order's token arrives
  const launchedOrderRef = useRef(null);      // the order the app was opened for automatically
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;
  const hiddenRetriesRef = useRef(0);         // expired hidden orders replaced since opening
  const startOrderRef = useRef(null);

  // ── QR timer ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (step === "polling" && bankId.qrStartToken && bankId.qrStartSecret) {
      // Sync the clock to when qrStartToken arrived — this approximates when the
      // BankID server created the order. startTimeRef was set before the HTTP call
      // so it is ahead by ~request latency, causing qr_time to run fast vs server.
      startTimeRef.current = Date.now();
      const tick = () => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setQrContent(computeQrContent(bankId.qrStartToken, bankId.qrStartSecret, elapsed));
      };
      tick();
      qrTimerRef.current = setInterval(tick, 1000);
      return () => clearInterval(qrTimerRef.current);
    }
  }, [step, bankId.qrStartToken, bankId.qrStartSecret]);

  // Keep orderRef in sync
  useEffect(() => {
    if (bankId.orderRef) orderRefRef.current = bankId.orderRef;
  }, [bankId.orderRef]);

  const isLink = purpose === "link";

  const callbacks = useCallback(() => ({
    purpose,
    onRegistrationRequired: (cd) => {
      completionDataRef.current = cd;
      clearInterval(qrTimerRef.current);
      setStep("reg_required");
    },
    onComplete: (token, _expireInSeconds, _completionData, info) => {
      clearInterval(qrTimerRef.current);
      setStep("idle");
      setResuming(false);
      onSuccess(token, info ?? {});
    },
    onFailed: (hint) => {
      clearInterval(qrTimerRef.current);
      setStep("idle");
      setResuming(false);
      const linkHint = { identityInUse: "auth.bankId.identityInUse", differentIdentity: "auth.bankId.differentIdentity" }[hint];
      setError(linkHint ? t(linkHint) : (HINTS[hint] ?? t("auth.bankId.failed")));
      // A hidden order nobody used ran out: replace it quietly (a couple of times at most).
      if (hiddenRef.current && hiddenRetriesRef.current < 2) {
        hiddenRetriesRef.current += 1;
        startOrderRef.current?.(1, { launch: false });
      }
    },
  }), [onSuccess, HINTS, t, purpose]);

  // ── Start an order for a tab ──────────────────────────────────────────────
  const startOrder = useCallback((tab, { launch }) => {
    setError("");
    setResuming(false);
    setShowAwaitingHelp(false);
    setStep("polling");
    startTimeRef.current = Date.now();
    setQrContent("");
    // Never on iPhone/iPad: iOS opens the app only from the user's own tap (the button below), and
    // a launch after the order returns lands on BankID's "couldn't be launched" page.
    launchWhenReadyRef.current = launch && tab === 1 && !isIOSDevice();

    bankId.start({
      mode: modeForTab(tab),
      scope,
      userVisibleData: t(isLink ? "auth.bankId.verifyMessage" : "auth.bankId.loginMessage"),
      ...callbacks(),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId.start, callbacks, scope, t, isLink]);
  startOrderRef.current = startOrder;

  // ── On open: resume this browser's order, or start one ────────────────────
  useEffect(() => {
    if (!open) return;
    hiddenRetriesRef.current = 0;
    if (resumeOrderRef) {
      setSubTab(1);
      setError("");
      setResuming(true);
      setStep("polling");
      launchWhenReadyRef.current = false;     // a resumed order is never relaunched
      bankId.resume(resumeOrderRef, callbacks());
    } else {
      const tab = initialTab ?? defaultTab;
      setSubTab(tab);
      startOrder(tab, { launch: autoLaunch });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && step === "reg_required") {
      const focusTimer = window.setTimeout(() => {
        emailInputRef.current?.focus?.();
      }, 120);

      return () => window.clearTimeout(focusTimer);
    }
  }, [open, step]);

  // ── Cleanup when modal closes ──────────────────────────────────────────────
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      // Only cancel if modal was previously open (avoid cancel on initial mount).
      // A finished order is no longer active in the hook, so this never cancels a success.
      clearInterval(qrTimerRef.current);
      bankId.cancel();
      if (resumeOrderRef) releaseResume(resumeOrderRef);
      launchWhenReadyRef.current = false;
      launchedOrderRef.current = null;
      setStep("idle");
      setResuming(false);
      setShowAwaitingHelp(false);
      setQrContent("");
      setError("");
      setRegEmail(""); setRegPersonalContact(""); setRegPassword(""); setRegConfirm(""); setRegError("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Cancel ─────────────────────────────────────────────────────────────────
  const handleCancel = useCallback(async () => {
    clearInterval(qrTimerRef.current);
    await bankId.cancel();
    setStep("idle");
    setError("");
    onClose();
  }, [bankId, onClose]);

  // ── Switch tab = switch order ──────────────────────────────────────────────
  const handleTabChange = useCallback((_, tab) => {
    if (tab === subTab || step === "reg_required") return;
    setSubTab(tab);
    clearInterval(qrTimerRef.current);
    bankId.cancel();
    // Choosing "This device" is an explicit choice to open the app here.
    startOrder(tab, { launch: tab === 1 });
  }, [bankId, startOrder, step, subTab]);

  // ── Open BankID on this device ─────────────────────────────────────────────
  // BankID's documented launch (app.bankid.com on mobile, bankid:/// on desktop), without the
  // deprecated "redirect" parameter: the server gave BankID the returnUrl for this order.
  const handleOpenOnDevice = useCallback(() => {
    if (bankId.autoStartToken) {
      launchedOrderRef.current = bankId.orderRef;
      openBankIdApp(bankId.autoStartToken);
    } else if (step === "idle") {
      // After a failure: a new order, opened as soon as it exists (this tap is the choice).
      startOrder(1, { launch: true });
    }
  }, [bankId.autoStartToken, bankId.orderRef, startOrder, step]);

  // Hidden again behind the login dialog (the user closed this one): a fresh order for the next tap.
  const wasHiddenRef = useRef(hidden);
  useEffect(() => {
    if (open && hidden && !wasHiddenRef.current) {
      clearInterval(qrTimerRef.current);
      bankId.cancel();
      setSubTab(1);
      startOrder(1, { launch: false });
    }
    wasHiddenRef.current = hidden;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hidden]);

  // For the dialog in front: open the app from the user's own tap, which is the only launch
  // iPhone/iPad allow. launch() is false when no same-device token is ready yet.
  useEffect(() => {
    if (!controlRef) return undefined;
    controlRef.current = {
      launch: () => {
        if (!open || subTab !== 1 || step !== "polling" || !bankId.autoStartToken || bankId.mode !== MODE_SAME_DEVICE) return false;
        launchedOrderRef.current = bankId.orderRef;
        openBankIdApp(bankId.autoStartToken);
        return true;
      },
      selectTab: (tab) => handleTabChange(null, tab),
      cancel: () => {
        clearInterval(qrTimerRef.current);
        bankId.cancel();
      },
    };
    return () => { controlRef.current = null; };
  });

  // Open the app by itself once this order's autoStartToken arrives (no second tap) — once per
  // order, and only after an explicit choice of this device.
  useEffect(() => {
    if (!open || subTab !== 1 || step !== "polling") return;
    if (!bankId.autoStartToken || bankId.mode !== MODE_SAME_DEVICE) return;
    if (!launchWhenReadyRef.current || launchedOrderRef.current === bankId.orderRef) return;
    launchWhenReadyRef.current = false;
    launchedOrderRef.current = bankId.orderRef;
    openBankIdApp(bankId.autoStartToken);
  }, [open, subTab, step, bankId.autoStartToken, bankId.mode, bankId.orderRef]);

  // Completed in the app but this browser not proven yet: after a short grace period, explain
  // how to recover (the return normally arrives within a second or two).
  useEffect(() => {
    if (bankId.hintCode !== HINT_AWAITING_RETURN) {
      setShowAwaitingHelp(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowAwaitingHelp(true), AWAITING_RETURN_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [bankId.hintCode]);

  // ── Registration submit ────────────────────────────────────────────────────
  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    setRegError("");
    if (regPassword !== regConfirm) {
      setRegError(t("auth.bankId.passwordMismatch"));
      return;
    }
    if (regPassword.length < 6) {
      setRegError(t("auth.bankId.passwordTooShort"));
      return;
    }
    if (!/^[0-9]+$/.test(regPersonalContact) || regPersonalContact.length < 10) {
      setRegError(t("auth.bankId.personalContactInvalid"));
      return;
    }
    bankId.submitRegistration(
      orderRefRef.current,
      regEmail,
      regPassword,
      regPersonalContact,
      false,
      completionDataRef.current,
      {
        onComplete: (token) => { setStep("idle"); onSuccess(token); },
        onFailed: (hint) => {
          setStep("idle");
          setError(HINTS[hint] ?? t("auth.bankId.failed"));
        },
      }
    );
  };

  const hintText = bankId.hintCode === HINT_AWAITING_RETURN
    ? (showAwaitingHelp ? HINTS.awaitingReturn : t("auth.bankId.waiting"))
    : resuming && !bankId.hintCode
      ? t("auth.bankId.resuming")
      : bankId.hintCode
        ? (HINTS[bankId.hintCode] ?? bankId.hintCode)
        : (subTab === 0 ? t("auth.bankId.scanQrHint") : t("auth.bankId.openAppHint"));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Portal>
    {open && !hidden && <Box sx={{
      position: "fixed", inset: 0, zIndex: 9999,
      overflow: "auto",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
    }}>
      <Box sx={{ width: "100%", maxWidth: 480, backgroundColor: SURFACE, border: `1px solid ${DS.ruleEdge}`, borderRadius: "13px", p: { xs: 3, sm: 4 }, boxShadow: "0 8px 32px rgba(16, 30, 40, 0.16)" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
          <IconButton onClick={handleCancel} size="small" sx={{ color: MUTED }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ textAlign: "center", mb: 3.5 }}>
          <Box sx={{ mb: 2 }}>
            <img src={bankIdLogo} alt="BankID" style={{ height: 36, objectFit: "contain" }} />
          </Box>
          <Typography sx={{ fontFamily: DS.serif, fontSize: "24px", fontWeight: 700, lineHeight: 1.18, letterSpacing: "-0.01em", color: TEXT, mb: 1 }}>
            {t(isLink ? "auth.bankId.verifyTitle" : "auth.bankId.modalTitle")}
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: MUTED, lineHeight: 1.6, maxWidth: 340, mx: "auto" }}>
            {t(isLink ? "auth.bankId.verifySubtitle" : "auth.bankId.modalSubtitle")}
          </Typography>
        </Box>

          {/* ── Sub-tabs: QR | This device ── */}
          {step !== "reg_required" && (
            <Box sx={{ backgroundColor: DS.pageZone, borderRadius: "10px", p: 0.5, mb: 3 }}>
              <Tabs
                value={subTab}
                onChange={handleTabChange}
                variant="fullWidth"
                TabIndicatorProps={{ style: { display: "none" } }}
                textColor="inherit"
                sx={{ minHeight: "unset" }}
              >
                <Tab label={t("auth.bankId.scanQr")}
                  sx={dsTabSx(subTab === 0)} />
                <Tab label={t("auth.bankId.thisDevice")}
                  sx={dsTabSx(subTab === 1)} />
              </Tabs>
            </Box>
          )}

          {/* ── Registration form ── */}
          {step === "reg_required" && (
            <Box>
              <Typography variant="body2" textAlign="center" color="text.secondary" mb={2} sx={{ color: MUTED }}>
                {t("auth.bankId.registrationWelcome")}
              </Typography>
              <form onSubmit={handleRegisterSubmit}>
                <Stack spacing={1.5}>
                  <TextField fullWidth label={t("auth.email")} type="email" size="small" sx={fieldSx}
                    value={regEmail} onChange={e => setRegEmail(e.target.value)} required autoFocus inputRef={emailInputRef} />
                  <TextField fullWidth label={t("auth.personalContact")} type="tel" size="small" sx={fieldSx}
                    value={regPersonalContact} onChange={e => setRegPersonalContact(e.target.value)} required />
                  <TextField fullWidth label={t("auth.password")} type="password" size="small" sx={fieldSx}
                    value={regPassword} onChange={e => setRegPassword(e.target.value)} required />
                  <TextField fullWidth label={t("auth.confirmPassword")} type="password" size="small" sx={fieldSx}
                    value={regConfirm} onChange={e => setRegConfirm(e.target.value)} required />
                  {regError && <Typography color="error" variant="body2" mb={1}>{regError}</Typography>}
                  {error && <Typography color="error" variant="body2" mb={1}>{error}</Typography>}
                  <Button fullWidth variant="contained" type="submit"
                    disabled={bankId.status === "pending"}
                    sx={inkButtonSx}>
                    {bankId.status === "pending"
                      ? <CircularProgress size={22} color="inherit" />
                      : t("auth.bankId.createAndSignIn")}
                  </Button>
                  <Button fullWidth variant="outlined" onClick={handleCancel}
                    sx={outlineButtonSx}>
                    {t("auth.bankId.cancel")}
                  </Button>
                </Stack>
              </form>
            </Box>
          )}

          {/* ── QR tab ── */}
          {step !== "reg_required" && subTab === 0 && (
            <Box textAlign="center">
              {step === "polling" && qrContent ? (
                <>
                  <Box sx={{ display: "inline-block", p: 2.5, border: `1px solid ${BORDER}`, borderRadius: "18px", mb: 2, backgroundColor: SURFACE }}>
                    <QRCode value={qrContent} size={220} />
                  </Box>
                  <Typography variant="body2" mb={1} sx={{ color: MUTED }}>
                    {t("auth.bankId.scanQrInstruction")}
                  </Typography>
                  <Typography variant="caption" sx={{ color: GREEN, fontWeight: 600 }}>
                    {hintText}
                  </Typography>
                </>
              ) : (
                <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress sx={{ color: GREEN }} />
                </Box>
              )}
              {error && <Typography color="error" variant="body2" mt={2}>{error}</Typography>}
              <Box mt={3}>
                {step === "idle" && (
                  <Button variant="contained" onClick={() => startOrder(0, { launch: false })}
                    sx={{ ...inkButtonSx, px: 5, mr: 1 }}>
                    {t("auth.bankId.tryAgain")}
                  </Button>
                )}
                <Button variant="outlined" onClick={handleCancel}
                  sx={{ ...outlineButtonSx, px: 5 }}>
                  {t("auth.bankId.cancel")}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── This device tab ── */}
          {step !== "reg_required" && subTab === 1 && (
            <Box textAlign="center">
              <Box sx={{ mb: 3, p: 3, backgroundColor: SURFACE_MUTED, borderRadius: "16px", border: `1px solid ${BORDER}` }}>
                <Typography variant="body2" mb={step === "polling" ? 1.5 : 0} sx={{ color: MUTED }}>
                  {t("auth.bankId.openAppHint")}
                </Typography>
                {step === "polling" && (
                  <Typography variant="body2" sx={{ color: GREEN, fontWeight: 600 }}>
                    {hintText}
                  </Typography>
                )}
              </Box>

              {error && <Typography color="error" variant="body2" mb={2}>{error}</Typography>}

              {/* Fallback if the automatic launch was blocked; after a failure it starts a new order. */}
              <Button fullWidth variant="contained" onClick={handleOpenOnDevice}
                disabled={step === "polling" && !bankId.autoStartToken}
                sx={{ ...inkButtonSx, mb: 2 }}>
                {step === "polling" && !bankId.autoStartToken
                  ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />{t("auth.bankId.waiting")}</>
                  : t("auth.bankId.openBankIdApp")}
              </Button>

              <Button variant="outlined" onClick={handleCancel}
                sx={{ ...outlineButtonSx, px: 5 }}>
                {t("auth.bankId.cancel")}
              </Button>
            </Box>
          )}

        <Divider sx={{ my: 3, borderColor: BORDER }} />
        <Typography variant="caption" sx={{ fontFamily: DS.sans, fontSize: "11px", color: DS.muted, lineHeight: 1.6, display: "block", textAlign: "center" }}>
          {t("auth.agreementTextStart")}{" "}
          <span style={{ color: TEXT, cursor: "pointer", fontWeight: 600 }}>{t("auth.termsOfService")}</span>{" "}
          {t("auth.agreementTextMiddle")}{" "}
          <span style={{ color: TEXT, cursor: "pointer", fontWeight: 600 }}>{t("auth.privacyPolicy")}</span>.
        </Typography>
      </Box>
    </Box>}
    </Portal>
  );
};

export default BankIdAuthModal;
